export type IdentityKind = 'program' | 'business' | 'internal';

export interface IdentityConfig {
  id: string;
  kind: IdentityKind;
  official: boolean;
  registerable: boolean;
  label: string;
  brandName: string;
  landingPath: string;
  dashboardPath?: string;
  logoutPath: string;
  authEntryPath: string;
  demoEnabled: boolean;
  aliases?: string[];
}

function createBusinessIdentity(
  id: string,
  label: string,
  brandName: string,
  options?: { aliases?: string[]; official?: boolean; registerable?: boolean; landingPath?: string }
): IdentityConfig {
  return {
    id,
    kind: 'business',
    official: options?.official ?? true,
    registerable: options?.registerable ?? true,
    label,
    brandName,
    landingPath: options?.landingPath ?? `/anchor-${id}`,
    dashboardPath: `/ecosystem/sector/${id}`,
    logoutPath: options?.landingPath ?? `/anchor-${id}`,
    authEntryPath: options?.landingPath ?? `/anchor-${id}`,
    demoEnabled: true,
    aliases: options?.aliases ?? [],
  };
}

export const IDENTITY_REGISTRY: IdentityConfig[] = [
  {
    id: 'anchor-ai-guard',
    kind: 'program',
    official: true,
    registerable: false,
    label: 'Anchor AI Guard Program',
    brandName: 'Anchor AI Guard',
    landingPath: '/anchor-ai-guard',
    dashboardPath: '/dashboard',
    logoutPath: '/anchor-ai-guard',
    authEntryPath: '/login',
    demoEnabled: true,
  },
  createBusinessIdentity('medical', 'Medical Intelligence', 'Anchor Medical Intelligence'),
  createBusinessIdentity('finance', 'Financial Guard', 'Anchor Financial Intelligence'),
  createBusinessIdentity('legal', 'Legal Shield', 'Anchor Legal Intelligence'),
  createBusinessIdentity('climate', 'Climate Defence', 'Anchor Climate Intelligence'),
  createBusinessIdentity('defence', 'Defence & National Security', 'Anchor Defence Intelligence'),
  createBusinessIdentity('energy', 'Energy Grid Protection', 'Anchor Energy Intelligence'),
  createBusinessIdentity('maritime', 'Maritime Command', 'Anchor Maritime Intelligence'),
  createBusinessIdentity('space', 'Space Operations', 'Anchor Space Intelligence'),
  createBusinessIdentity(
    'agriculture',
    'Agriculture Intelligence',
    'Anchor Agriculture Intelligence'
  ),
  createBusinessIdentity('education', 'Education Security', 'Anchor Education Intelligence'),
  createBusinessIdentity('leo', 'Law Enforcement Operations', 'Anchor LEO Intelligence', {
    aliases: ['l-e-o'],
  }),
  createBusinessIdentity('wildlife', 'Wildlife Conservation', 'Anchor Wildlife Intelligence'),
  createBusinessIdentity(
    'casino',
    'Casino & Hospitality Intelligence',
    'Anchor Casino Intelligence'
  ),
  createBusinessIdentity('interpol', 'Interpol Intelligence', 'Anchor Interpol Intelligence'),
  createBusinessIdentity('aviation', 'Aviation Security', 'Anchor Aviation Intelligence'),
  createBusinessIdentity('charity', 'Charity & NGO Intelligence', 'Anchor Charity Intelligence'),
  createBusinessIdentity('crypto', 'Crypto & DeFi', 'Anchor Crypto Intelligence'),
  createBusinessIdentity(
    'entertainment',
    'Entertainment Intelligence',
    'Anchor Entertainment Intelligence'
  ),
  createBusinessIdentity('mining', 'Mining Intelligence', 'Anchor Mining Intelligence'),
  createBusinessIdentity('resources', 'Resources & Minerals', 'Anchor Resources Intelligence'),
  createBusinessIdentity('retail', 'Retail Security', 'Anchor Retail Intelligence'),
  createBusinessIdentity('sports', 'Sports Intelligence', 'Anchor Sports Intelligence'),
  createBusinessIdentity('automotive', 'Automotive Defence', 'Anchor Automotive Intelligence'),
  createBusinessIdentity('insurance', 'Insurance Intelligence', 'Anchor Insurance Intelligence'),
  createBusinessIdentity('logistics', 'Logistics & Supply Chain', 'Anchor Logistics Intelligence'),
  createBusinessIdentity('smartcity', 'Smart City Operations', 'Anchor Smart City Intelligence', {
    aliases: ['smart-city'],
  }),
  createBusinessIdentity('social', 'Social Intelligence', 'Anchor Social Intelligence'),
  createBusinessIdentity('water', 'Water Security', 'Anchor Water Intelligence', {
    aliases: ['water-utilities'],
  }),
  createBusinessIdentity(
    'infrastructure',
    'Infrastructure Security',
    'Anchor Infrastructure Intelligence'
  ),
  createBusinessIdentity('biotech', 'Biotech Intelligence', 'Anchor Biotechnology Intelligence'),
  createBusinessIdentity('bioagri', 'Bio-Agriculture', 'Anchor BioAgri Intelligence', {
    aliases: ['bio-agri'],
  }),
  createBusinessIdentity('agroshield', 'AgroShield', 'Anchor AgroShield Intelligence', {
    aliases: ['agro-shield'],
  }),
  createBusinessIdentity(
    'semiconductor',
    'Semiconductor Intelligence',
    'Anchor Semiconductor Intelligence'
  ),
  createBusinessIdentity('telecom', 'Telecom Security', 'Anchor Telecom Intelligence'),
  createBusinessIdentity('fuel', 'Fuel Intelligence', 'Anchor Fuel Intelligence'),
  createBusinessIdentity('blackflight', 'Blackflight Taskforce', 'Anchor Blackflight Intelligence'),
  createBusinessIdentity(
    'sovereign-surveillance',
    'Sovereign Surveillance',
    'Anchor Sovereign Surveillance Intelligence'
  ),
  createBusinessIdentity('humankind', 'HumanKind Intelligence', 'Anchor HumanKind Intelligence', {
    aliases: ['human-kind'],
  }),
  createBusinessIdentity('quantum', 'Quantum Cryptography', 'Anchor Quantum Intelligence'),
  createBusinessIdentity('kinetic', 'Kinetic Intelligence', 'Anchor Kinetic Intelligence'),
  createBusinessIdentity('agri', 'Agri Intelligence', 'Anchor Agri Precision Intelligence'),
  createBusinessIdentity(
    'godseye',
    "God's Eye Global Protection",
    'Anchor GodsEye Global Protection',
    {
      official: false,
      registerable: true,
      aliases: ['gods-eye'],
      landingPath: '/anchor-godseye',
    }
  ),
  createBusinessIdentity(
    'trade-risk',
    'Trade Risk Intelligence',
    'Anchor Trade Risk Intelligence',
    {
      official: false,
      registerable: true,
      aliases: ['traderisk'],
      landingPath: '/anchor-traderisk',
    }
  ),
];

const identityMap = new Map<string, IdentityConfig>();
const aliasMap = new Map<string, string>();

for (const config of IDENTITY_REGISTRY) {
  identityMap.set(config.id, config);
  aliasMap.set(config.id, config.id);
  for (const alias of config.aliases ?? []) {
    aliasMap.set(alias, config.id);
  }
}

export function normalizeIdentityId(value: string): string {
  return value.toLowerCase().trim().replace(/_/g, '-').replace(/\s+/g, '-');
}

export function resolveIdentityId(value: string | null | undefined): string | null {
  if (!value) return null;
  const normalized = normalizeIdentityId(value);
  return aliasMap.get(normalized) ?? normalized;
}

export function resolveBusinessIdentityId(value: string | null | undefined): string | null {
  const resolved = resolveIdentityId(value);
  if (!resolved) return null;
  const config = identityMap.get(resolved);
  if (!config || config.kind === 'program') return null;
  return config.id;
}

export function getIdentityConfig(value: string | null | undefined): IdentityConfig | undefined {
  const resolved = resolveIdentityId(value);
  return resolved ? identityMap.get(resolved) : undefined;
}

export function getOfficialIdentityConfigs(): IdentityConfig[] {
  return IDENTITY_REGISTRY.filter((config) => config.official);
}

export function getOfficialBusinessIdentities(): IdentityConfig[] {
  return IDENTITY_REGISTRY.filter((config) => config.kind === 'business' && config.official);
}

export function getRegisterableSectorIds(): string[] {
  const values = new Set<string>();
  for (const config of IDENTITY_REGISTRY) {
    if (!config.registerable || config.kind === 'program') continue;
    values.add(config.id);
    for (const alias of config.aliases ?? []) {
      values.add(alias);
    }
  }
  return [...values];
}

export function getSectorBrandNames(): Record<string, string> {
  const names: Record<string, string> = {};
  for (const config of IDENTITY_REGISTRY) {
    if (!config.registerable || config.kind === 'program') continue;
    names[config.id] = config.brandName;
    for (const alias of config.aliases ?? []) {
      names[alias] = config.brandName;
    }
  }
  return names;
}

export function getLandingViewId(value: string): string {
  const config = getIdentityConfig(value);
  if (!config) return `anchor-${normalizeIdentityId(value)}`;
  return config.kind === 'program' ? 'anchor-ai-guard' : `anchor-${config.id}`;
}

export function getDashboardViewId(value: string): string {
  const config = getIdentityConfig(value);
  if (!config) return `sector-${normalizeIdentityId(value)}`;
  return config.kind === 'program' ? 'dashboard' : `sector-${config.id}`;
}

function getAnchorAliases(config: IdentityConfig): string[] {
  if (config.kind === 'program') return [];
  return (config.aliases ?? []).map((alias) => `/anchor-${alias}`);
}

function getSectorAliases(config: IdentityConfig): string[] {
  if (!config.dashboardPath || config.kind === 'program') return [];
  return (config.aliases ?? []).map((alias) => `/ecosystem/sector/${alias}`);
}

function getLegacyAnchorAliases(config: IdentityConfig): string[] {
  if (config.kind !== 'business') return [];
  const legacy = [`/ecosystem/anchor/${config.id}`];
  for (const alias of config.aliases ?? []) {
    legacy.push(`/ecosystem/anchor/${alias}`);
  }
  return legacy;
}

export interface ResolvedIdentityRoute {
  identity: IdentityConfig;
  appView: string;
  path: string;
  routeKind: 'landing' | 'dashboard';
}

export function resolveIdentityRoute(
  pathname: string | null | undefined
): ResolvedIdentityRoute | null {
  if (!pathname) return null;
  const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');

  for (const config of IDENTITY_REGISTRY) {
    if (path === config.landingPath || getAnchorAliases(config).includes(path)) {
      return {
        identity: config,
        appView: getLandingViewId(config.id),
        path: config.landingPath,
        routeKind: 'landing',
      };
    }

    if (path !== config.landingPath && getLegacyAnchorAliases(config).includes(path)) {
      return {
        identity: config,
        appView: getLandingViewId(config.id),
        path: config.landingPath,
        routeKind: 'landing',
      };
    }

    if (
      config.dashboardPath &&
      (path === config.dashboardPath || getSectorAliases(config).includes(path))
    ) {
      return {
        identity: config,
        appView: getDashboardViewId(config.id),
        path: config.dashboardPath,
        routeKind: 'dashboard',
      };
    }
  }

  return null;
}

export function resolveIdentityView(view: string | null | undefined): ResolvedIdentityRoute | null {
  if (!view) return null;
  if (view === 'anchor-ai-guard') {
    return resolveIdentityRoute('/anchor-ai-guard');
  }
  if (view === 'dashboard') {
    return resolveIdentityRoute('/dashboard');
  }

  if (view.startsWith('anchor-')) {
    const identity = resolveBusinessIdentityId(view.slice('anchor-'.length));
    return identity ? resolveIdentityRoute(`/anchor-${identity}`) : null;
  }

  if (view.startsWith('sector-')) {
    const identity = resolveBusinessIdentityId(view.slice('sector-'.length));
    return identity ? resolveIdentityRoute(`/ecosystem/sector/${identity}`) : null;
  }

  return null;
}
